'use client';

import React from 'react';
import { AlertTriangle, CheckCircle2, Info, RefreshCw, X, XCircle } from 'lucide-react';
import { cn } from '../utils/cn';
import { Toast as ToastType } from './toast-context';

type ToastProps = {
  toast: ToastType;
  onClose: (id: string) => void;
};

const TYPE_CONFIG = {
  success: {
    Icon: CheckCircle2,
    iconClass: 'text-emerald-500',
    borderClass: 'border-emerald-500/20',
    glowClass: 'shadow-[0_4px_24px_rgba(34,197,94,0.10)]',
  },
  error: {
    Icon: XCircle,
    iconClass: 'text-red-500',
    borderClass: 'border-red-500/25',
    glowClass: 'shadow-[0_4px_24px_rgba(239,68,68,0.12)]',
  },
  warning: {
    Icon: AlertTriangle,
    iconClass: 'text-amber-500',
    borderClass: 'border-amber-500/20',
    glowClass: 'shadow-[0_4px_24px_rgba(245,158,11,0.10)]',
  },
  info: {
    Icon: Info,
    iconClass: 'text-blue-400',
    borderClass: 'border-blue-400/20',
    glowClass: 'shadow-[0_4px_24px_rgba(96,165,250,0.10)]',
  },
} as const;

type ToastTypeKey = keyof typeof TYPE_CONFIG;

export const Toast = ({ toast, onClose }: ToastProps) => {
  const { id, message, description, type, refreshAction } = toast;
  const { Icon, iconClass, borderClass, glowClass } =
    TYPE_CONFIG[type as ToastTypeKey] ?? TYPE_CONFIG.info;

  return (
    <div
      className={cn(
        'group relative flex w-full items-start gap-3 overflow-hidden',
        'rounded-xl border px-3.5 py-3',
        'glass-surface-static-bg',
        borderClass,
        glowClass,
        'animate-in slide-in-from-bottom-2 fade-in-0 duration-300 ease-out',
      )}
      role="alert"
    >
      <div className={cn('mt-0.5 shrink-0', iconClass)}>
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium leading-snug text-foreground">{message}</p>
        {description && (
          <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
        )}
        {refreshAction && (
          <button
            onClick={refreshAction.onRefresh}
            className="mt-0.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground focus:outline-none focus-visible:underline"
          >
            <RefreshCw className="h-3 w-3" />
            {refreshAction.label}
          </button>
        )}
      </div>

      <button
        onClick={() => onClose(id)}
        className="mt-0.5 shrink-0 rounded-md p-0.5 text-muted-foreground/50 transition-colors duration-150 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
